/*
 * Copyright (C) 2020 Graylog, Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the Server Side Public License, version 1,
 * as published by MongoDB, Inc.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * Server Side Public License for more details.
 *
 * You should have received a copy of the Server Side Public License
 * along with this program. If not, see
 * <http://www.mongodb.com/licensing/server-side-public-license>.
 */
import type { VisualizationType } from 'views/types';
import ScatterVisualization from 'views/components/visualizations/scatter/ScatterVisualization';
import { hasAtLeastOneMetric } from 'views/components/visualizations/validations';
import type { AxisType } from 'views/logic/aggregationbuilder/visualizations/XYVisualization';
import { DEFAULT_AXIS_TYPE, axisTypes } from 'views/logic/aggregationbuilder/visualizations/XYVisualization';
import ScatterVisualizationConfig from 'views/logic/aggregationbuilder/visualizations/ScatterVisualizationConfig';

type ScatterVisualizationConfigFormValues = {
  axisType: AxisType,
};

const validate = hasAtLeastOneMetric('Scatter plot');

const scatterChart: VisualizationType<typeof ScatterVisualization.type, ScatterVisualizationConfig, ScatterVisualizationConfigFormValues> = {
  type: ScatterVisualization.type,
  displayName: 'Scatter Plot',
  component: ScatterVisualization,
  config: {
    createConfig: () => ({ axisType: DEFAULT_AXIS_TYPE }),
    fromConfig: (config) => ({ axisType: config?.axisType ?? DEFAULT_AXIS_TYPE }),
    toConfig: (formValues) => ScatterVisualizationConfig.create(formValues.axisType),
    fields: [{
      name: 'axisType',
      title: 'Axis Type',
      type: 'select',
      options: axisTypes,
      required: true,
    }],
  },
  capabilities: ['event-annotations'],
  validate,
};

export default scatterChart;
